import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'informed';
import { useCurrentRoute, useNavigation } from 'react-navi';
import { NewPassword, recoveryPassword, useApi } from '@repo-level/core-front';

import { ModalDefaultProps } from 'components/Modal';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { ModalTemplate } from 'components/ModalTemplate';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Button } from 'components/buttons';
import { Password } from 'components/form/Password';
import { composeValidators, passValidator, required } from 'helpers/validators';
import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';

import { ScNewPassword } from './styled';
import { useMedia } from 'modules/common/hooks/useMedia';

const validatePassword = composeValidators(required, passValidator);

const RESET_PASSWORD_TOKEN = 'reset_password_token';

export const EditPasswordModal = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const {
      t,
      i18n: { language },
    } = useTranslation('common');

    const [error, setError] = useState<string>();

    const { isMobileS } = useMedia();

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? { type: 'mobile', mode: { type: 'default' } }
        : {
            type: 'desktop',
            size: 'small',
            mode: { type: 'default' },
          };
    }, [isMobileS]);

    const {
      url: { query },
    } = useCurrentRoute();

    const { navigate } = useNavigation();

    const [requestRecoveryPassword, { pending }] = useApi(recoveryPassword);

    const handleClose = useCallback(() => {
      navigate('/');
      onClose && onClose();
    }, [onClose, navigate]);

    const handleSubmit = useCallback(
      async (values: NewPassword) => {
        if (values.password !== values.passwordConfirmation) {
          setError('common:auth.recovery.passwordsNotMatch');

          return;
        }

        const { error } = await requestRecoveryPassword({
          ...values,
          resetPasswordToken: query[RESET_PASSWORD_TOKEN],
        }).request;

        if (error != null) {
          showTooltip({
            type: TooltipTypes.ERROR,
            translate: true,
            title: `${t('common:auth.recovery.expiredTokenError')}`,
          });

          handleClose();

          return;
        }

        navigate('/users/sign_in');
      },
      [requestRecoveryPassword, navigate, query, handleClose, t]
    );

    return (
      <ModalTemplate
        ref={ref}
        onClose={handleClose}
        settings={modalSettings}
        title={t('auth.recovery.changeYourPassword')}
      >
        <ScNewPassword>
          <Form<NewPassword> onSubmit={handleSubmit}>
            <FormGroup>
              <Password
                field="password"
                validate={validatePassword}
                validateOnChange
                validateOnBlur
                strengthChecker
                animatedPlaceholder={t('auth.recovery.newPassword')}
              />
            </FormGroup>
            <FormGroup>
              <Password
                field="passwordConfirmation"
                animatedPlaceholder={t('auth.recovery.confirmPassword')}
                error={error && t(error)}
              />
            </FormGroup>

            <Button
              className={language}
              type="submit"
              green
              xLarge
              pending={pending}
            >
              {t('auth.recovery.changeMyPassword')}
            </Button>
          </Form>
        </ScNewPassword>
      </ModalTemplate>
    );
  }
);
